import React, { createContext, useCallback, useContext, useMemo, useState } from 'react';

import { ColorMode } from './palettes';

/**
 * Color-mode (light/dark) context for the Robinhood-bright theme.
 *
 * Holds the active `mode`, persists it to `localStorage` under
 * `farchat-color-mode`, and exposes a `toggle`/`setMode` API. The active
 * MUI theme is derived from `mode` by the app shell (see `App.tsx`).
 */

const STORAGE_KEY = 'farchat-color-mode';

export interface ColorModeContextValue {
  mode: ColorMode;
  toggle: () => void;
  setMode: (mode: ColorMode) => void;
}

const ColorModeContext = createContext<ColorModeContextValue | undefined>(undefined);

/**
 * Read the persisted mode, defaulting to `'light'` when storage is empty,
 * unavailable, or holds anything other than exactly `'light'`/`'dark'`.
 */
function readStoredMode(): ColorMode {
  try {
    const stored = window.localStorage.getItem(STORAGE_KEY);
    if (stored === 'light' || stored === 'dark') {
      return stored;
    }
  } catch {
    // localStorage may be unavailable (SSR, private mode, tests) — fall back.
  }
  return 'light';
}

/** Persist the mode; swallow errors so storage-less envs don't crash. */
function persistMode(mode: ColorMode): void {
  try {
    window.localStorage.setItem(STORAGE_KEY, mode);
  } catch {
    // Ignore — persistence is best-effort.
  }
}

export const ColorModeProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const [mode, setModeState] = useState<ColorMode>(() => readStoredMode());

  const setMode = useCallback((next: ColorMode) => {
    setModeState((prev) => {
      if (prev === next) {
        return prev;
      }
      persistMode(next);
      return next;
    });
  }, []);

  const toggle = useCallback(() => {
    setModeState((prev) => {
      const next: ColorMode = prev === 'light' ? 'dark' : 'light';
      persistMode(next);
      return next;
    });
  }, []);

  const value = useMemo<ColorModeContextValue>(
    () => ({ mode, toggle, setMode }),
    [mode, toggle, setMode]
  );

  return <ColorModeContext.Provider value={value}>{children}</ColorModeContext.Provider>;
};

/** Access the color-mode context. Throws if used outside `ColorModeProvider`. */
export function useColorMode(): ColorModeContextValue {
  const ctx = useContext(ColorModeContext);
  if (ctx === undefined) {
    throw new Error('useColorMode must be used within a ColorModeProvider');
  }
  return ctx;
}

export default ColorModeContext;
